import { useEffect, useState } from 'react';
import { supabase } from '../../lib/supabase';
import {
  Button,
  Spinner,
  Tab,
} from '../../components/design-system';
import { AdminPage } from './AdminPage';

interface FeedbackRow {
  id: string;
  user_id: string | null;
  user_email: string | null;
  user_name: string | null;
  user_role: string | null;
  message: string;
  archived: boolean;
  archive_reason: string | null;
  archived_at: string | null;
  archived_by: string | null;
  created_at: string;
}

type FeedbackTab = 'active' | 'archived';

export function AdminFeedback() {
  const [items, setItems] = useState<FeedbackRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState<FeedbackTab>('active');
  const [search, setSearch] = useState('');
  const [archiving, setArchiving] = useState<FeedbackRow | null>(null);
  const [archiveReason, setArchiveReason] = useState('');
  const [saving, setSaving] = useState(false);
  const [expanded, setExpanded] = useState<Set<string>>(new Set());

  async function load() {
    const { data } = await supabase
      .from('feedbacks')
      .select('*')
      .order('created_at', { ascending: false });
    setItems((data ?? []) as FeedbackRow[]);
    setLoading(false);
  }

  useEffect(() => { void load(); }, []);

  async function handleArchive() {
    if (!archiving) return;
    setSaving(true);
    await supabase.from('feedbacks').update({
      archived: true,
      archive_reason: archiveReason.trim() || null,
      archived_at: new Date().toISOString(),
      archived_by: (await supabase.auth.getUser()).data.user?.id ?? null,
    }).eq('id', archiving.id);
    setArchiving(null);
    setArchiveReason('');
    setSaving(false);
    await load();
  }

  async function handleUnarchive(id: string) {
    await supabase.from('feedbacks').update({
      archived: false,
      archive_reason: null,
      archived_at: null,
      archived_by: null,
    }).eq('id', id);
    await load();
  }

  function toggleExpanded(id: string) {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }

  const filtered = items.filter((i) => {
    if (tab === 'active' && i.archived) return false;
    if (tab === 'archived' && !i.archived) return false;
    if (search) {
      const q = search.toLowerCase();
      return (
        i.message.toLowerCase().includes(q) ||
        (i.user_name ?? '').toLowerCase().includes(q) ||
        (i.user_email ?? '').toLowerCase().includes(q)
      );
    }
    return true;
  });

  const activeCount = items.filter((i) => !i.archived).length;
  const archivedCount = items.filter((i) => i.archived).length;

  if (loading) return <AdminPage title="Feedback"><div className="flex justify-center py-16"><Spinner /></div></AdminPage>;

  return (
    <AdminPage
      title="Feedback"
      description={`${activeCount} active · ${archivedCount} archived`}
    >
      {/* Tabs + search */}
      <div className="flex flex-col sm:flex-row gap-3 mb-6">
        <div className="flex gap-2 shrink-0">
          <Tab active={tab === 'active'} onClick={() => setTab('active')}>
            Active ({activeCount})
          </Tab>
          <Tab active={tab === 'archived'} onClick={() => setTab('archived')}>
            Archived ({archivedCount})
          </Tab>
        </div>
        <div className="relative flex-1 max-w-sm">
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-4 h-4 text-app-muted absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none">
            <path fillRule="evenodd" d="M9 3.5a5.5 5.5 0 100 11 5.5 5.5 0 000-11zM2 9a7 7 0 1112.452 4.391l3.328 3.329a.75.75 0 11-1.06 1.06l-3.329-3.328A7 7 0 012 9z" clipRule="evenodd" />
          </svg>
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search feedback…"
            className="w-full rounded-full border border-app-border bg-app-bg pl-9 pr-3 py-2 text-sm text-app-fg placeholder:text-app-muted focus:border-gold focus:outline-none"
          />
        </div>
      </div>

      {/* List */}
      {filtered.length === 0 ? (
        <div className="text-center py-16">
          <p className="text-sm text-app-muted">
            {items.length === 0 ? 'No feedback received yet.' : 'No feedback matches your search.'}
          </p>
        </div>
      ) : (
        <div className="flex flex-col gap-2">
          {filtered.map((item) => {
            const isExpanded = expanded.has(item.id);
            const isLong = item.message.length > 200;
            return (
              <div key={item.id} className="bg-app-card border border-app-border rounded-xl px-5 py-4">
                {/* Header row */}
                <div className="flex items-start justify-between gap-3 mb-2">
                  <div className="flex items-center gap-2 min-w-0">
                    <div className="w-8 h-8 rounded-full bg-app-bg border border-app-border flex items-center justify-center text-xs font-semibold text-app-muted shrink-0">
                      {(item.user_name ?? 'A')[0].toUpperCase()}
                    </div>
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-app-fg truncate">
                        {item.user_name ?? 'Anonymous'}
                        {item.user_role && (
                          <span className="ml-1.5 text-[10px] uppercase tracking-wide text-app-muted bg-app-bg border border-app-border rounded-full px-2 py-0.5">
                            {item.user_role}
                          </span>
                        )}
                      </p>
                      <p className="text-xs text-app-muted">{item.user_email ?? 'No email'}</p>
                    </div>
                  </div>
                  <span className="text-xs text-app-muted shrink-0">
                    {new Date(item.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                  </span>
                </div>

                {/* Message */}
                <div className="text-sm text-app-fg whitespace-pre-wrap leading-relaxed">
                  {isLong && !isExpanded ? (
                    <>
                      {item.message.slice(0, 200)}…
                      <button onClick={() => toggleExpanded(item.id)} className="text-xs text-violet ml-1 hover:underline">Show more</button>
                    </>
                  ) : (
                    <>
                      {item.message}
                      {isLong && <button onClick={() => toggleExpanded(item.id)} className="text-xs text-violet ml-1 hover:underline">Show less</button>}
                    </>
                  )}
                </div>

                {/* Archive reason (if archived) */}
                {item.archived && item.archive_reason && (
                  <div className="mt-3 px-3 py-2 rounded-lg bg-app-bg border border-app-border">
                    <p className="text-xs text-app-muted">
                      <span className="font-medium">Archived:</span> {item.archive_reason}
                    </p>
                  </div>
                )}

                {/* Actions */}
                <div className="flex gap-3 mt-3 pt-3 border-t border-app-border">
                  {!item.archived ? (
                    <button
                      onClick={() => { setArchiving(item); setArchiveReason(''); }}
                      className="text-xs text-app-muted hover:text-app-fg transition-colors"
                    >
                      Archive
                    </button>
                  ) : (
                    <button
                      onClick={() => void handleUnarchive(item.id)}
                      className="text-xs text-app-muted hover:text-app-fg transition-colors"
                    >
                      Unarchive
                    </button>
                  )}
                  {item.user_email && (
                    <a
                      href={`mailto:${item.user_email}`}
                      className="text-xs text-violet/70 hover:text-violet transition-colors"
                    >
                      Reply by email
                    </a>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Archive modal */}
      {archiving && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-end md:items-center justify-center" onClick={() => setArchiving(null)}>
          <div
            className="bg-app-card md:border md:border-app-border rounded-t-3xl md:rounded-2xl w-full max-w-md pb-safe-or-4"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="px-5 pt-5 pb-3 border-b border-app-border flex items-center justify-between gap-3">
              <h3 className="text-lg font-semibold text-app-fg">Archive feedback</h3>
              <button onClick={() => setArchiving(null)} className="w-8 h-8 rounded-full bg-app-bg border border-app-border text-app-muted flex items-center justify-center hover:text-app-fg shrink-0">
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-4 h-4">
                  <path d="M6.28 5.22a.75.75 0 00-1.06 1.06L8.94 10l-3.72 3.72a.75.75 0 101.06 1.06L10 11.06l3.72 3.72a.75.75 0 101.06-1.06L11.06 10l3.72-3.72a.75.75 0 00-1.06-1.06L10 8.94 6.28 5.22z" />
                </svg>
              </button>
            </div>
            <div className="px-5 py-5 flex flex-col gap-4">
              <div className="text-sm text-app-muted bg-app-bg border border-app-border rounded-xl px-4 py-3 line-clamp-3 whitespace-pre-wrap">
                {archiving.message}
              </div>
              <div className="flex flex-col gap-1">
                <label className="text-sm font-medium text-app-fg-2">Why are you archiving this?</label>
                <textarea
                  value={archiveReason}
                  onChange={(e) => setArchiveReason(e.target.value)}
                  rows={2}
                  autoFocus
                  placeholder="Already addressed, duplicate, not actionable…"
                  className="w-full rounded-xl border border-app-border bg-app-bg px-3 py-2.5 text-sm text-app-fg placeholder:text-app-muted focus:border-gold focus:outline-none resize-none"
                />
              </div>
              <div className="flex items-center justify-end gap-3 pt-2">
                <button onClick={() => setArchiving(null)} className="text-sm text-app-muted hover:text-app-fg">Cancel</button>
                <Button onClick={() => void handleArchive()} disabled={saving}>
                  {saving ? 'Archiving…' : 'Archive'}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </AdminPage>
  );
}

export default AdminFeedback;
